import React from 'react'

import { Box, Color, DisplayPosition, withCitrusTheme } from '@gousto-internal/citrus-react'

import type { StandardRecipeItem } from '@library/api-menu-experience'

import { BasketButton } from '../BasketButton'
import { CustomisationLabel } from './CustomisationLabel'
import { RecipeSurcharge } from './RecipeSurcharge'
import { SubtitleTagRow } from './SubtitleTagRow'
import { RecipeTitle } from './RecipeTitle'
import { SoldOutOverlay } from '../SoldOutOverlay'
import type { RecipeTileActionProps } from '../RecipeTile.types'

type RecipeTileBodyProps = {
  recipe: StandardRecipeItem
  isOutOfStock: boolean
  isCustomisationOpen?: boolean
  isBasketButtonDisabled?: boolean
  onCustomiseClick?: () => void
  showFlexPortions?: boolean
} & Omit<RecipeTileActionProps, 'quantity'>

const RecipeTileBodyWrapper = withCitrusTheme(Box, () => ({
  boxSizing: 'border-box',
  paddingTop: '16px',
}))

const MetadataContainer = withCitrusTheme(Box, (theme) => ({
  paddingTop: `${theme.spacing(2)}px`,
}))

export const RecipeTileBody = ({
  recipe,
  isOutOfStock,
  isInBasket,
  canAddRecipes,
  reachedLimit,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  isCustomisationOpen = false,
  isBasketButtonDisabled = false,
  onCustomiseClick,
  showFlexPortions = false,
}: RecipeTileBodyProps) => {
  const customisationLabel = recipe.customisations?.label
  const preventRecipeDetailsOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
  }

  return (
    <RecipeTileBodyWrapper
      bg={Color.White}
      data-testid="recipe-tile-body"
      flexBasis="0%"
      flexGrow={1}
      flexShrink={1}
      minHeight="100px"
      paddingBottom={4}
      paddingH={4}
      position={DisplayPosition.Relative}
    >
      {!showFlexPortions && (
        <BasketButton
          recipe={recipe}
          isInBasket={isInBasket}
          isOutOfStock={isOutOfStock}
          canAddRecipes={canAddRecipes}
          reachedLimit={reachedLimit}
          isDisabled={isBasketButtonDisabled}
          onAddClick={onAddClick}
          onRemoveClick={onRemoveClick}
          onBlockedAddAttempt={onBlockedAddAttempt}
        />
      )}
      <SoldOutOverlay isOutOfStock={isOutOfStock} />
      <RecipeTitle title={recipe.title} />
      <MetadataContainer
        data-testid="recipe-tile-metadata-container"
        style={{ paddingRight: customisationLabel ? '96px' : undefined }}
      >
        <SubtitleTagRow recipe={recipe} />
      </MetadataContainer>
      <Box
        bottom="8px"
        data-testid="recipe-tile-customise-container"
        onClick={preventRecipeDetailsOpen}
        position={DisplayPosition.Absolute}
        right="16px"
      >
        <CustomisationLabel
          recipe={recipe}
          isOpen={isCustomisationOpen}
          onClick={onCustomiseClick}
        />
      </Box>
      <RecipeSurcharge recipe={recipe} isOutOfStock={isOutOfStock} />
    </RecipeTileBodyWrapper>
  )
}
